import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/types/content";
import { categoryName, formatViews } from "@/lib/content";

export interface HeroSlide {
  work: Work;
  banner: string;
  label: string;
}

interface HeroCarouselProps {
  slides: HeroSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6800);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const go = (next: number) => setIndex((next + slides.length) % slides.length);
  const active = slides[index];

  return (
    <section className="relative w-full overflow-hidden bg-background-100">
      <div className="relative h-[500px] w-full md:h-[600px]">
        {slides.map((slide, slideIndex) => (
          <div
            key={slide.work.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              slideIndex === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.banner}
              alt={`${slide.work.title} 焦点画面`}
              title={`${slide.work.title} · 亚洲无码高清成人影视`}
              className="h-full w-full object-cover object-center"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/75 to-background-950/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-950/40" />

        <div className="relative mx-auto flex h-full w-full max-w-[1440px] flex-col justify-end px-4 pb-16 md:px-8 md:pb-20">
          <div key={active.work.id} className="fade-up max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-500/60 bg-accent-950/60 px-3 py-1 text-[11px] tracking-wide text-accent-200">
              <i className="ri-fire-line" />
              {active.label}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-50 md:text-[44px]">
              {active.work.title}
            </h2>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-foreground-300">
              <span className="rounded bg-background-50/10 px-2 py-0.5 text-primary-300">
                {categoryName(active.work.category)}
              </span>
              <span>{active.work.region}</span>
              <span className="text-foreground-600">|</span>
              <span>{active.work.year}</span>
              <span className="text-foreground-600">|</span>
              <span>{active.work.episodeCount} 话</span>
              <span className="text-foreground-600">|</span>
              <span className="flex items-center gap-1 text-primary-300">
                <i className="ri-star-fill text-[10px]" />
                {active.work.rating.toFixed(1)}
              </span>
              <span className="text-foreground-600">|</span>
              <span>{formatViews(active.work.views)} 浏览</span>
            </div>
            <p className="mt-4 line-clamp-2 max-w-xl text-[13px] leading-relaxed text-foreground-300 md:text-sm">
              {active.work.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.work.id}`}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-950 transition hover:bg-primary-400"
              >
                <i className="ri-file-text-line" />
                查看作品资料
              </Link>
              <Link
                to={`/category/${active.work.category}`}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-foreground-100/25 px-5 py-2.5 text-sm text-foreground-100 transition hover:border-primary-400/70 hover:text-primary-300"
              >
                更多{categoryName(active.work.category)}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-5 right-4 flex items-center gap-2 md:bottom-8 md:right-8">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-foreground-100/25 bg-background-950/40 text-foreground-100 transition hover:border-primary-400 hover:text-primary-300"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-foreground-100/25 bg-background-950/40 text-foreground-100 transition hover:border-primary-400 hover:text-primary-300"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>

        <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 md:flex">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.work.id}
              type="button"
              aria-label={`切换到 ${slide.work.title}`}
              onClick={() => go(slideIndex)}
              className={`h-1 cursor-pointer rounded-full transition-all duration-500 ${
                slideIndex === index ? "w-8 bg-primary-500" : "w-3 bg-foreground-100/30 hover:bg-foreground-100/60"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent" />
    </section>
  );
}